
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { motion, cubicBezier } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { blogPosts } from '@shared/data/blogData';
import { authors } from '@shared/data/authorsData';
import { AppPage } from '../types';
import { CDN_URLS } from '@shared/config';
import { getImageUrl, getAvatarUrl } from '../utils/imageUtils';
import Footer from '../components/Footer';

interface AuthorPageProps {
  authorId: string;
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const AuthorPage: React.FC<AuthorPageProps> = ({ authorId, onNavigate }) => {
  const { t } = useTranslation();

  const author = authors.find((a: any) => a.id === authorId);
  const postsByAuthor = blogPosts.filter((p: any) => p.authorId === authorId);

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1), staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };


  // Helper for blog post images which are usually on GCS

  if (!author) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
        <h1 className="text-2xl font-serif">Author not found.</h1>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-4xl w-full bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 md:p-10 mb-8 border border-gray-200 dark:border-gray-700">
        <motion.div variants={itemVariants} className="flex flex-col md:flex-row items-center gap-8 mb-10">
          <img src={getAvatarUrl(author.avatarUrl)} alt={t(author.nameKey)} className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700 shadow-md" />
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white leading-tight">
              {t(author.nameKey)}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">
              {t(author.bioKey)}
            </p>
          </div>
        </motion.div>

        <motion.section variants={sectionVariants}>
          <h2 className="text-3xl font-serif font-semibold text-gray-900 dark:text-white mb-6 border-b border-gray-300 dark:border-gray-600 pb-3">
            {t('authorPage_postsBy', { authorName: t(author.nameKey) })}
          </h2>
          <div className="space-y-8">
            {postsByAuthor.length > 0 ? postsByAuthor.map((post: any) => (
              <motion.div
                key={post.id}
                variants={itemVariants}
                className="group grid grid-cols-1 sm:grid-cols-4 gap-4 items-center"
              >
                <div className="sm:col-span-1 rounded-lg overflow-hidden shadow-md">
                  <img
                    src={getImageUrl(post.imageUrl)}
                    alt={t(post.titleKey)}
                    className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="sm:col-span-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t(post.dateKey || '')}</p>
                  <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    <button onClick={() => onNavigate('blog-post', { postId: post.id })} className="text-left">
                      {t(post.titleKey)}
                    </button>
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {t(post.excerptKey)}
                  </p>
                </div>
              </motion.div>
            )) : (
              <p className="text-gray-500 dark:text-gray-400">{t('authorPage_noPosts')}</p>
            )}
          </div>
        </motion.section>
      </div>
      <Footer onNavigate={onNavigate} />
    </motion.div>
  );
};

export default AuthorPage;
